/* DMCA Take-Down — public submission form per 17 U.S.C. § 512(c)(3).

   Any copyright owner who believes content hosted on verqo (seller
   storefronts, line sheets, product imagery) infringes their work can
   send a notice through this page. Submissions land in `dmca_notices`
   for our designated agent to review.

   ⚠️ You must ALSO register your designated agent with the U.S.
   Copyright Office (https://www.copyright.gov/dmca-directory/) — $6
   fee, mandatory to claim safe-harbor under § 512(c)(2). */
import { Link } from "react-router-dom";
import { Scale, ArrowLeft, Check } from "lucide-react";
import { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function Dmca() {
  const [form, setForm] = useState({
    type: "takedown",
    complainant_name: "", complainant_company: "", complainant_email: "",
    complainant_phone: "", complainant_address: "",
    rights_holder: "", original_work_url: "",
    infringing_url: "", infringing_description: "",
    good_faith:     false, accurate:        false, authorized: false,
    signature: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["complainant_name", "complainant_email", "complainant_phone",
                      "complainant_address", "rights_holder", "infringing_url", "signature"];
    for (const k of required) {
      if (!form[k]) return toast.error(`Please fill: ${k.replace(/_/g, " ")}`);
    }
    if (!form.good_faith || !form.accurate || !form.authorized) {
      return toast.error("All three statements under penalty of perjury must be acknowledged.");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/legal/dmca", form);
      setDone(data.ref);
      toast.success("DMCA notice received. Our agent will review within 48 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please email legal@verqo.com instead.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-dmca">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to verqo
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <Scale className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · DMCA Safe Harbor</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">DMCA Take-Down Notice</h1>
        <div className="text-[12px] text-foreground/55 mt-2">17 U.S.C. § 512 · v1.0</div>

        <div className="mt-6 space-y-3 text-sm text-foreground/80 leading-relaxed">
          <p>
            Use this form to submit a take-down notice under the Digital Millennium
            Copyright Act. To be effective under § 512(c)(3), your notice must
            include every required element below and be signed by you (or your
            authorized agent) under penalty of perjury.
          </p>
          <p>
            Counter-notices may be sent by email to{" "}
            <a className="underline" href="mailto:legal@verqo.com">legal@verqo.com</a>.
            Repeat-infringer accounts will be terminated per our policy.
          </p>
        </div>

        {done ? (
          <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-3" data-testid="dmca-confirmation">
            <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center shrink-0">
              <Check className="w-4 h-4"/>
            </div>
            <div>
              <div className="font-semibold text-emerald-900">Notice received.</div>
              <div className="text-sm text-emerald-900/85 mt-1">
                Reference <code className="font-mono">{done}</code>. Our designated agent
                will review and respond within 48 hours.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 rounded-2xl border border-border bg-white p-6 space-y-5" data-testid="dmca-form">
            <Section title="1. Who are you?">
              <Field label="Full legal name *">
                <input value={form.complainant_name} onChange={e => set("complainant_name", e.target.value)} data-testid="dmca-name" required className="legal-input"/>
              </Field>
              <Field label="Company (if filing on behalf of one)">
                <input value={form.complainant_company} onChange={e => set("complainant_company", e.target.value)} data-testid="dmca-company" className="legal-input"/>
              </Field>
              <Field label="Email *">
                <input type="email" value={form.complainant_email} onChange={e => set("complainant_email", e.target.value)} data-testid="dmca-email" required className="legal-input"/>
              </Field>
              <Field label="Phone *">
                <input value={form.complainant_phone} onChange={e => set("complainant_phone", e.target.value)} data-testid="dmca-phone" required className="legal-input"/>
              </Field>
              <Field label="Mailing address *">
                <textarea rows={2} value={form.complainant_address} onChange={e => set("complainant_address", e.target.value)} data-testid="dmca-address" required className="legal-input"/>
              </Field>
            </Section>

            <Section title="2. What work is infringed?">
              <Field label="Copyright holder (you, your company, or your client) *">
                <input value={form.rights_holder} onChange={e => set("rights_holder", e.target.value)} data-testid="dmca-rights" required className="legal-input"/>
              </Field>
              <Field label="URL or description of the original work">
                <textarea rows={2} value={form.original_work_url} onChange={e => set("original_work_url", e.target.value)} data-testid="dmca-original" className="legal-input"/>
              </Field>
            </Section>

            <Section title="3. What is the infringing material?">
              <Field label="Exact URL(s) on verqo *">
                <textarea rows={2} value={form.infringing_url} onChange={e => set("infringing_url", e.target.value)} data-testid="dmca-url" required className="legal-input" placeholder="https://verqo.com/s/handle/products/abc"/>
              </Field>
              <Field label="Brief description (what to remove, which image / paragraph)">
                <textarea rows={3} value={form.infringing_description} onChange={e => set("infringing_description", e.target.value)} data-testid="dmca-desc" className="legal-input"/>
              </Field>
            </Section>

            <Section title="4. Required statements (penalty of perjury)">
              <Statement
                checked={form.good_faith}
                onChange={v => set("good_faith", v)}
                testId="dmca-good-faith"
                text="I have a good-faith belief that the use of the material is not authorized by the copyright owner, its agent, or the law."
              />
              <Statement
                checked={form.accurate}
                onChange={v => set("accurate", v)}
                testId="dmca-accurate"
                text="The information in this notice is accurate and I am authorized to act on behalf of the owner of the exclusive right that is allegedly infringed."
              />
              <Statement
                checked={form.authorized}
                onChange={v => set("authorized", v)}
                testId="dmca-authorized"
                text="I understand that I may be liable for damages, including attorney's fees, if I knowingly materially misrepresent that material is infringing (17 U.S.C. § 512(f))."
              />
            </Section>

            <Section title="5. Signature">
              <Field label="Type your full legal name as your electronic signature *">
                <input value={form.signature} onChange={e => set("signature", e.target.value)} data-testid="dmca-signature" required className="legal-input"/>
              </Field>
            </Section>

            <button type="submit" disabled={busy} data-testid="dmca-submit"
              className="h-11 px-5 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50">
              {busy ? "Submitting…" : "Submit DMCA notice"}
            </button>
            <p className="text-[11px] text-foreground/55">
              Designated DMCA agent: <strong>[YOUR DESIGNATED AGENT NAME]</strong> ·
              <a className="underline ml-1" href="mailto:dmca@verqo.com">dmca@verqo.com</a>.
              Notices may also be sent by mail to <strong>[POSTAL ADDRESS]</strong>.
            </p>
          </form>
        )}
      </div>
      {/* tiny utility class for inputs so we don't repeat */}
      <style>{`.legal-input{display:block;width:100%;height:40px;padding:0 10px;border:1px solid rgb(0 0 0 / 0.15);border-radius:8px;background:#fff;font-size:13px}.legal-input[rows]{height:auto;padding:8px 10px;font-family:inherit}`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55 mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[12px] text-foreground/65 mb-1">{label}</div>
      {children}
    </label>
  );
}
function Statement({ checked, onChange, testId, text }) {
  return (
    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${checked ? "border-emerald-500/40 bg-emerald-50/40" : "border-foreground/15 bg-secondary/30 hover:border-foreground/40"}`} data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5 accent-emerald-600 shrink-0"/>
      <span className="text-[12px] text-foreground/80 leading-snug">{text}</span>
    </label>
  );
}
